"use client"

import { Spinner } from "@medusajs/icons"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"

const iconButtonVariants = cva(
  "transition-fg inline-flex w-fit items-center justify-center overflow-hidden rounded-md outline-none disabled:bg-ui-bg-disabled disabled:shadow-buttons-neutral disabled:text-ui-fg-disabled",
  {
    variants: {
      variant: {
        primary:
          "shadow-buttons-neutral text-ui-fg-subtle bg-ui-button-neutral hover:bg-ui-button-neutral-hover active:bg-ui-button-neutral-pressed focus-visible:shadow-buttons-neutral-focus",
        transparent:
          "text-ui-fg-subtle bg-ui-button-transparent hover:bg-ui-button-transparent-hover active:bg-ui-button-transparent-pressed focus-visible:shadow-buttons-neutral-focus focus-visible:bg-ui-bg-base disabled:!bg-transparent disabled:!shadow-none",
      },
      size: {
        "2xsmall": "h-5 w-5",
        xsmall: "h-6 w-6 p-1",
        small: "h-7 w-7 p-1",
        base: "h-8 w-8 p-1.5",
        large: "h-10 w-10 p-2.5",
        xlarge: "h-12 w-12 p-3.5",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "base",
    },
  }
)

export type IconButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof iconButtonVariants> & {
    asChild?: boolean
    isLoading?: boolean
  }

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { className, variant, size, asChild = false, children, isLoading, disabled, ...props },
    ref
  ) => {
    const Component = asChild ? Slot : "button"

    const renderInner = () => {
      if (isLoading) {
        return (
          <span className="pointer-events-none">
            <div className="bg-ui-bg-disabled absolute inset-0 flex items-center justify-center rounded-md">
              <Spinner className="animate-spin" />
            </div>
            {children}
          </span>
        )
      }
      return children
    }

    return (
      <Component
        ref={ref}
        className={cn(iconButtonVariants({ variant, size }), className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {renderInner()}
      </Component>
    )
  }
)
IconButton.displayName = "IconButton"

export { IconButton, iconButtonVariants }
